import React, { useState } from "react";
import styled from "styled-components";
import {
  Card,
  Table,
  Row,
  Col,
  Tag,
  Tooltip,
  Button,
  DatePicker,
  Select,
  Space,
} from "antd";
import {
  CheckCircleOutlined,
  CheckOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  RollbackOutlined,
  ExclamationCircleOutlined,
  ExclamationOutlined,
  EyeOutlined,
  MessageOutlined,
  UserOutlined,
  HomeOutlined,
  DollarOutlined,
  PlusOutlined,
  FilterOutlined,
  EditOutlined,
} from "@ant-design/icons";
import { DetailsModal } from "../shared";
import { useBreakpoints } from "../../lib/breakpoints";
import { trackEvent } from "../../helpers/mixpanel";
import InvoiceAdjustmentModal from "../invoice/InvoiceAdjustmentModal";

const { RangePicker } = DatePicker;
const { Option } = Select;

const PaymentDetailsSection = ({
  payments,
  totalPaymentsCount,
  units,
  tenants,
  loading,
  statusFilter,
  selectedUnit,
  onChangeStatusFilter,
  onChangeUnitFilter,
  onChangeDateRange,
  onMarkPaid,
  onMarkOverdue,
  onMarkPending,
  renderEmptyState,
  onAddPayment,
  onSendReminder,
  sendingReminderToPaymentId,
  onDeletePayment,
  onRefresh,
}) => {
  const { isMobile } = useBreakpoints();

  const formatShortDate = (date) => {
    if (!date) return "N/A";
    const d = date?.toDate?.() || new Date(date);
    if (Number.isNaN(d.getTime())) return "N/A";
    return d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const [selectedPayment, setSelectedPayment] = useState(null);
  const [isDetailsModalVisible, setIsDetailsModalVisible] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [isTenantModalVisible, setIsTenantModalVisible] = useState(false);
  const [selectedUnitForModal, setSelectedUnitForModal] = useState(null);
  const [isUnitModalVisible, setIsUnitModalVisible] = useState(false);
  const [pageSize, setPageSize] = useState(10);
  const [isAdjustmentModalVisible, setIsAdjustmentModalVisible] =
    useState(false);
  const [selectedPaymentForAdjustment, setSelectedPaymentForAdjustment] =
    useState(null);

  const handleViewDetails = (payment) => {
    trackEvent("payment_view_details", {
      paymentId: payment.id,
      status: payment.status,
    });
    setSelectedPayment(payment);
    setIsDetailsModalVisible(true);
  };

  const handleCloseDetailsModal = () => {
    setIsDetailsModalVisible(false);
    setSelectedPayment(null);
  };

  const handleViewTenant = (tenant) => {
    trackEvent("payment_view_tenant", {
      tenantId: tenant?.id,
      paymentId: payments.find((p) => p.tenantId === tenant?.id)?.id,
    });
    setSelectedTenant(tenant);
    setIsTenantModalVisible(true);
  };

  const handleCloseTenantModal = () => {
    setIsTenantModalVisible(false);
    setSelectedTenant(null);
  };

  const handleViewUnit = (unit) => {
    trackEvent("payment_view_unit", {
      unitId: unit?.id,
      paymentId: payments.find((p) => p.unitId === unit?.id)?.id,
    });
    setSelectedUnitForModal(unit);
    setIsUnitModalVisible(true);
  };

  const handleCloseUnitModal = () => {
    setIsUnitModalVisible(false);
    setSelectedUnitForModal(null);
  };

  const handlePageSizeChange = (current, size) => {
    trackEvent("payment_table_page_size_change", {
      pageSize: size,
      currentPage: current,
    });
    setPageSize(size);
  };

  // Calculate final total including adjustments
  const calculateFinalTotal = (payment) => {
    const baseAmount = payment?.amount || 0;
    const adjustments = payment?.adjustments || [];
    const adjustmentTotal = adjustments.reduce(
      (sum, adj) => sum + (adj.amount || 0),
      0
    );
    return baseAmount + adjustmentTotal;
  };

  // Check if payment can have adjustments (must match backend: unpaid, pending, or overdue only)
  const canAddAdjustment = (payment) => {
    const status = payment?.status?.toLowerCase();
    return status === "unpaid" || status === "pending" || status === "overdue";
  };

  const handleAddAdjustment = (payment) => {
    trackEvent("payment_add_adjustment_clicked", {
      paymentId: payment.id,
      status: payment.status,
    });
    setSelectedPaymentForAdjustment(payment);
    setIsAdjustmentModalVisible(true);
  };

  const handleAdjustmentSuccess = (result) => {
    trackEvent("payment_adjustment_added", {
      paymentId: selectedPaymentForAdjustment?.id,
    });
    handleCloseAdjustmentModal();
    // Trigger refresh if callback provided
    if (onRefresh) {
      onRefresh();
    }
  };

  const handleCloseAdjustmentModal = () => {
    setIsAdjustmentModalVisible(false);
    setSelectedPaymentForAdjustment(null);
  };

  // Determine if we have payments but they're all filtered out
  const hasPaymentsButFiltered =
    payments.length === 0 && (statusFilter !== "all" || selectedUnit !== "all");

  // Create different empty states
  const renderFilteredEmptyState = () => {
    return (
      <div style={{ textAlign: "center", padding: "3rem 2rem" }}>
        <FilterOutlined
          style={{ fontSize: "3rem", color: "#d9d9d9", marginBottom: "1rem" }}
        />
        <h3 style={{ color: "#666", marginBottom: "0.5rem" }}>
          No Payments Match Your Filters
        </h3>
        <p style={{ color: "#999", marginBottom: "1.5rem" }}>
          Try adjusting your filters to see more payments, or add a new payment.
        </p>
        <Button type="primary" icon={<PlusOutlined />} onClick={onAddPayment}>
          {!isMobile ? "Add Payment" : ""}
        </Button>
      </div>
    );
  };

  // Function to determine row className based on payment status
  const getRowClassName = (record) => {
    if (record.status === "overdue") {
      return "overdue-payment-row";
    }
    if (record.status === "failed") {
      return "failed-payment-row";
    }
    return "";
  };

  const getActionButtons = (payment) => {
    const buttons = [];

    if (payment.status === "pending") {
      buttons.push({
        text: "Mark as Paid",
        type: "primary",
        icon: (
          <CheckCircleOutlined style={{ filter: "brightness(0) invert(1)" }} />
        ),
        onClick: () => {
          onMarkPaid(payment.id);
          handleCloseDetailsModal();
        },
      });
      buttons.push({
        text: "Mark as Overdue",
        type: "default",
        icon: <ExclamationCircleOutlined />,
        danger: true,
        onClick: () => {
          onMarkOverdue(payment.id);
          handleCloseDetailsModal();
        },
      });
    }

    if (payment.status === "overdue") {
      buttons.push({
        text: "Mark as Paid",
        type: "primary",
        icon: (
          <CheckCircleOutlined style={{ filter: "brightness(0) invert(1)" }} />
        ),
        onClick: () => {
          onMarkPaid(payment.id);
          handleCloseDetailsModal();
        },
      });
      buttons.push({
        text: "Mark as Pending",
        type: "default",
        icon: <ClockCircleOutlined />,
        onClick: () => {
          onMarkPending(payment.id);
          handleCloseDetailsModal();
        },
      });
    }

    if (payment.status === "failed") {
      buttons.push({
        text: "Mark as Paid",
        type: "primary",
        icon: (
          <CheckCircleOutlined style={{ filter: "brightness(0) invert(1)" }} />
        ),
        onClick: () => {
          onMarkPaid(payment.id);
          handleCloseDetailsModal();
        },
      });
    }

    const ps = (payment.status || "").toLowerCase();
    if (ps !== "paid" && ps !== "processing" && ps !== "succeeded") {
      buttons.push({
        text: "Send Reminder",
        type: "default",
        icon: <MessageOutlined />,
        loading: sendingReminderToPaymentId === payment.id,
        onClick: () => {
          if (onSendReminder) {
            onSendReminder(payment.id);
          }
          handleCloseDetailsModal();
        },
      });
    }

    // Add delete button for all payment statuses
    if (onDeletePayment) {
      buttons.push({
        text: "Delete",
        type: "default",
        danger: true,
        onClick: () => {
          onDeletePayment(payment.id);
          handleCloseDetailsModal();
        },
      });
    }

    return buttons;
  };
  const getStatusColor = (status) => {
    const s = (status || "").toLowerCase();
    switch (s) {
      // Green: succeeded, paid
      case "succeeded":
      case "paid":
        return "success";
      // Blue: pending, pending_verification, processing, created
      case "pending":
      case "pending_verification":
      case "processing":
      case "created":
        return "processing"; // Use 'processing' instead of 'warning' to avoid Ant Design's yellow
      // Red: failed, canceled, overdue
      case "failed":
      case "canceled":
      case "overdue":
        return "error";
      // Purple: refunded
      case "refunded":
        return "default";
      default:
        return "default";
    }
  };

  const getStatusIcon = (status) => {
    const s = (status || "").toLowerCase();
    switch (s) {
      // Success statuses
      case "succeeded":
      case "paid":
        return <CheckCircleOutlined />;
      // Pending/processing statuses
      case "pending":
      case "processing":
      case "created":
        return <ClockCircleOutlined />;
      // Verification required
      case "pending_verification":
        return <ExclamationCircleOutlined />;
      // Failed/error statuses
      case "failed":
      case "overdue":
        return <ExclamationCircleOutlined />;
      // Canceled
      case "canceled":
        return <CloseCircleOutlined />;
      // Refunded
      case "refunded":
        return <RollbackOutlined />;
      default:
        return <ClockCircleOutlined />;
    }
  };

  const columns = [
    {
      title: "Tenant",
      dataIndex: "tenantId",
      key: "tenantId",
      sorter: (a, b) => {
        const tenantA = tenants.find((t) => t.id === a.tenantId);
        const tenantB = tenants.find((t) => t.id === b.tenantId);
        const nameA = tenantA?.name || "Unknown Tenant";
        const nameB = tenantB?.name || "Unknown Tenant";
        return nameA.localeCompare(nameB);
      },
      render: (tenantId) => {
        const tenant = tenants.find((t) => t.id === tenantId);
        return tenant ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              cursor: "pointer",
              color: "var(--color-primary)",
              fontWeight: 500,
            }}
            onClick={() => handleViewTenant(tenant)}
            onMouseEnter={(e) => (e.target.style.textDecoration = "underline")}
            onMouseLeave={(e) => (e.target.style.textDecoration = "none")}
          >
            <UserOutlined style={{ color: "var(--color-primary)" }} />
            <span>{tenant.name || "Unknown Tenant"}</span>
          </div>
        ) : (
          "Unknown Tenant"
        );
      },
    },
    {
      title: "Door",
      dataIndex: "unitId",
      key: "unitId",
      sorter: (a, b) => {
        const unitA = units.find((u) => u.id === a.unitId);
        const unitB = units.find((u) => u.id === b.unitId);
        const nameA = unitA?.name || "Unknown Door";
        const nameB = unitB?.name || "Unknown Door";
        return nameA.localeCompare(nameB);
      },
      render: (unitId) => {
        const unit = units.find((u) => u.id === unitId);
        return unit ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              cursor: "pointer",
              color: "var(--color-primary)",
              fontWeight: 500,
            }}
            onClick={() => handleViewUnit(unit)}
            onMouseEnter={(e) => (e.target.style.textDecoration = "underline")}
            onMouseLeave={(e) => (e.target.style.textDecoration = "none")}
          >
            <HomeOutlined style={{ color: "var(--color-primary)" }} />
            <span>{unit.name}</span>
          </div>
        ) : (
          "Unknown Door"
        );
      },
    },
    {
      title: "Total Due",
      dataIndex: "amount",
      key: "totalDue",
      sorter: (a, b) => calculateFinalTotal(a) - calculateFinalTotal(b),
      render: (amount, record) => {
        const finalTotal = calculateFinalTotal(record);
        const adjustments = record?.adjustments || [];
        const hasAdjustments = adjustments.length > 0;

        return (
          <div>
            <div style={{ fontWeight: 500 }}>
              $
              {finalTotal.toLocaleString("en-US", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </div>
            {hasAdjustments && (
              <Tooltip
                title={
                  <div>
                    <div>
                      Base: $
                      {(record.amount || 0).toLocaleString("en-US", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </div>
                    {adjustments.map((adj, idx) => (
                      <div key={idx}>
                        {adj.description}: {adj.amount >= 0 ? "+" : ""}$
                        {adj.amount.toLocaleString("en-US", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </div>
                    ))}
                  </div>
                }
              >
                <span
                  style={{ fontSize: "11px", color: "#8c8c8c", cursor: "help" }}
                >
                  {adjustments.length} adjustment
                  {adjustments.length !== 1 ? "s" : ""}
                </span>
              </Tooltip>
            )}
          </div>
        );
      },
    },
    {
      title: "Due Date",
      dataIndex: "dueDate",
      key: "dueDate",
      sorter: (a, b) => {
        const dateA = a.dueDate?.toDate?.() || new Date(a.dueDate);
        const dateB = b.dueDate?.toDate?.() || new Date(b.dueDate);
        return dateA.getTime() - dateB.getTime();
      },
      defaultSortOrder: "descend",
      render: (date) => {
        return formatShortDate(date);
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      sorter: (a, b) => {
        const rank = (st) => {
          const x = (st || "").toLowerCase();
          if (x === "overdue" || x === "failed") return 1;
          if (x === "processing" || x === "pending_verification") return 2;
          if (x === "pending" || x === "created") return 3;
          if (x === "paid" || x === "succeeded") return 4;
          return 5;
        };
        return rank(a.status) - rank(b.status);
      },
      render: (status, record) => {
        const st = (status || "").toLowerCase();
        // Get human-readable status text
        const statusLabels = {
          created: "Created",
          pending: "Pending",
          pending_verification: "Verification Required",
          processing: "Processing",
          succeeded: "Paid",
          paid: "Paid",
          failed: "Failed",
          canceled: "Canceled",
          refunded: "Refunded",
          overdue: "Overdue",
        };

        // Official backend status descriptions
        const statusDescriptions = {
          created:
            "Payment record created. Awaiting tenant to initiate payment.",
          pending: "Payment has not been received yet. Awaiting tenant to pay.",
          pending_verification:
            "Bank account verification required. Please complete microdeposits verification to proceed.",
          processing:
            "Payment is being processed. Tenant has initiated payment. This may take 1-3 business days for ACH payments.",
          succeeded: "Payment has been successfully processed and confirmed.",
          paid: "Payment has been successfully processed and confirmed.",
          failed:
            "Payment failed. Please reach out to your tenant to try the payment again.",
          canceled: "Payment was canceled.",
          refunded: "Payment was refunded.",
          overdue:
            "Payment is past the due date and has not been received. Consider sending a reminder.",
        };

        const statusText =
          statusLabels[st] ||
          (status
            ? String(status).charAt(0).toUpperCase() + String(status).slice(1)
            : "Unknown");
        const tooltipText =
          statusDescriptions[st] || `Payment status: ${statusText}`;

        return (
          <Tooltip title={tooltipText} trigger={["hover", "click"]}>
            <span style={{ cursor: "help" }}>
              <Tag color={getStatusColor(st)} icon={getStatusIcon(st)}>
                {statusText}
              </Tag>
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: "Payment Method",
      dataIndex: "method",
      key: "method",
      sorter: (a, b) => {
        const methodA = a.method || "";
        const methodB = b.method || "";
        return methodA.localeCompare(methodB);
      },
      render: (method) => {
        if (!method) return "-";

        // Capitalize the first letter and handle common payment methods
        const capitalized =
          method.charAt(0).toUpperCase() + method.slice(1).toLowerCase();

        // Handle specific payment method abbreviations
        if (method.toLowerCase() === "ach") return "ACH";
        if (method.toLowerCase() === "check") return "Check";
        if (method.toLowerCase() === "cash") return "Cash";
        if (method.toLowerCase() === "credit") return "Credit";
        if (method.toLowerCase() === "debit") return "Debit";
        if (method.toLowerCase() === "wire") return "Wire";
        if (method.toLowerCase() === "zelle") return "Zelle";
        if (method.toLowerCase() === "venmo") return "Venmo";
        if (method.toLowerCase() === "paypal") return "PayPal";

        return capitalized;
      },
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Button.Group size="small">
          <Tooltip title="View Details">
            <Button
              icon={<EyeOutlined />}
              onClick={() => handleViewDetails(record)}
            />
          </Tooltip>
          {canAddAdjustment(record) && (
            <Tooltip title="Add Credit/Adjustment">
              <Button
                icon={<EditOutlined />}
                onClick={() => handleAddAdjustment(record)}
                style={{ color: "#1890ff" }}
              />
            </Tooltip>
          )}
          {(record.status === "pending" ||
            record.status === "overdue" ||
            record.status === "failed") && (
            <Tooltip title="Mark as Paid">
              <Button
                icon={<CheckCircleOutlined />}
                onClick={() => onMarkPaid(record.id)}
              />
            </Tooltip>
          )}
          {(() => {
            const rs = (record.status || "").toLowerCase();
            if (rs === "paid" || rs === "processing" || rs === "succeeded")
              return null;
            return (
            <Tooltip title="Send Reminder">
              <Button
                icon={<MessageOutlined />}
                loading={sendingReminderToPaymentId === record.id}
                onClick={() => {
                  console.log(
                    "[DEBUG] Send reminder button clicked in PaymentDetailsSection for payment:",
                    record.id
                  );
                  if (onSendReminder) {
                    onSendReminder(record.id);
                  }
                }}
              />
            </Tooltip>
            );
          })()}
        </Button.Group>
      ),
    },
  ];

  return (
    <TableSection>
      <Card
        title={`Payment Details${
          totalPaymentsCount !== undefined ? ` (${totalPaymentsCount})` : ""
        }`}
        style={{ borderRadius: "16px", position: "relative" }}
        extra={
          <AddPaymentButton
            type="primary"
            icon={<PlusOutlined />}
            onClick={onAddPayment}
          >
            {!isMobile ? "Add Payment" : ""}
          </AddPaymentButton>
        }
      >
        <FiltersRow gutter={[16, 16]}>
          <Col xs={24} sm={12} md={8}>
            <FilterInput
              placeholder={["Start Date", "End Date"]}
              onChange={onChangeDateRange}
            />
          </Col>
          <Col xs={24} sm={12} md={8}>
            <FilterSelect
              placeholder="Filter by Status"
              value={statusFilter}
              onChange={onChangeStatusFilter}
            >
              <Option value="all">All Statuses</Option>
              <Option value="paid">Paid</Option>
              <Option value="pending">
                Pending &amp; in progress (incl. processing)
              </Option>
              <Option value="processing">Processing only</Option>
              <Option value="overdue">Overdue</Option>
            </FilterSelect>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <FilterSelect
              placeholder="Filter by Door"
              value={selectedUnit}
              onChange={onChangeUnitFilter}
            >
              <Option value="all">All Doors</Option>
              {units.map((unit) => (
                <Option key={unit.id} value={unit.id}>
                  {unit.name}
                </Option>
              ))}
            </FilterSelect>
          </Col>
        </FiltersRow>

        {isMobile ? (
          payments.length === 0 ? (
            hasPaymentsButFiltered ? (
              renderFilteredEmptyState()
            ) : (
              renderEmptyState()
            )
          ) : (
            <MobilePaymentCards>
              {payments.map((payment) => (
                <PaymentCard key={payment.id} $status={payment.status}>
                  <CardHeader>
                    <StatusIndicator $status={payment.status}>
                      {getStatusIcon(payment.status)}
                    </StatusIndicator>
                    <CardTitle>
                      <TenantInfo
                        onClick={() =>
                          handleViewTenant(
                            tenants.find((t) => t.id === payment.tenantId)
                          )
                        }
                      >
                        <UserOutlined />
                        <span>
                          {tenants.find((t) => t.id === payment.tenantId)
                            ?.name || "Unknown Tenant"}
                        </span>
                      </TenantInfo>
                      <UnitInfo
                        onClick={() =>
                          handleViewUnit(
                            units.find((u) => u.id === payment.unitId)
                          )
                        }
                      >
                        <HomeOutlined />
                        <span>
                          {units.find((u) => u.id === payment.unitId)?.name ||
                            "Unknown Door"}
                        </span>
                      </UnitInfo>
                    </CardTitle>
                  </CardHeader>

                  <CardContent>
                    <AmountSection>
                      <AmountLabel>
                        {payment?.adjustments?.length > 0
                          ? "Total Due"
                          : "Rent Amount"}
                      </AmountLabel>
                      <AmountValue>
                        $
                        {calculateFinalTotal(payment).toLocaleString("en-US", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </AmountValue>
                      {payment?.adjustments?.length > 0 && (
                        <>
                          <AdjustmentsSummary>
                            {payment.adjustments.length} adjustment
                            {payment.adjustments.length !== 1 ? "s" : ""}
                          </AdjustmentsSummary>
                          <MobileAdjustmentsList>
                            {(payment.adjustments || []).map((adj, idx) => (
                              <MobileAdjustmentRow key={idx}>
                                {adj.description || "Adjustment"}:{" "}
                                {adj.amount >= 0 ? "+" : ""}$
                                {(adj.amount ?? 0).toLocaleString("en-US", {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}
                              </MobileAdjustmentRow>
                            ))}
                          </MobileAdjustmentsList>
                        </>
                      )}
                    </AmountSection>

                    <DetailsSection>
                      <DetailRow>
                        <DetailLabel>Due Date</DetailLabel>
                        <DetailValue>
                          {payment.dueDate
                            ? (
                                payment.dueDate?.toDate?.() ||
                                new Date(payment.dueDate)
                              ).toLocaleDateString()
                            : "N/A"}
                        </DetailValue>
                      </DetailRow>

                      <DetailRow>
                        <DetailLabel>Payment Method</DetailLabel>
                        <DetailValue>
                          {payment.method
                            ? payment.method.charAt(0).toUpperCase() +
                              payment.method.slice(1).toLowerCase()
                            : "-"}
                        </DetailValue>
                      </DetailRow>

                      <StatusDetailRow>
                        <StatusDetailLabel>Status</StatusDetailLabel>
                        <StatusDetailContent>
                          <StatusTag color={getStatusColor(payment.status)}>
                            {(() => {
                              const statusLabels = {
                                created: "Created",
                                pending: "Pending",
                                pending_verification: "Verification Required",
                                processing: "Processing",
                                succeeded: "Paid",
                                paid: "Paid",
                                failed: "Failed",
                                canceled: "Canceled",
                                refunded: "Refunded",
                                overdue: "Overdue",
                              };
                              return (
                                statusLabels[payment.status] ||
                                payment.status.charAt(0).toUpperCase() +
                                  payment.status.slice(1)
                              );
                            })()}
                          </StatusTag>
                          <StatusDescription>
                            {(() => {
                              const statusDescriptions = {
                                created:
                                  "Payment created and awaiting processing.",
                                pending:
                                  "Payment pending and waiting to be processed.",
                                pending_verification:
                                  "Bank verification required.",
                                processing:
                                  "Processing (1-3 business days for ACH).",
                                succeeded:
                                  "Successfully processed and confirmed.",
                                paid: "Successfully processed and confirmed.",
                                failed: "Payment failed. Please try again.",
                                canceled: "Payment was canceled.",
                                refunded: "Payment was refunded.",
                                overdue:
                                  "Past due date. Consider sending a reminder.",
                              };
                              return statusDescriptions[payment.status] || "";
                            })()}
                          </StatusDescription>
                        </StatusDetailContent>
                      </StatusDetailRow>
                    </DetailsSection>

                    <ActionButtons>
                      <ActionButton
                        type="primary"
                        icon={
                          <EyeOutlined
                            style={{ filter: "brightness(0) invert(1)" }}
                          />
                        }
                        onClick={() => handleViewDetails(payment)}
                      >
                        View Details
                      </ActionButton>

                      {canAddAdjustment(payment) && (
                        <ActionButton
                          type="default"
                          icon={<EditOutlined />}
                          onClick={() => handleAddAdjustment(payment)}
                        >
                          Add Adjustment
                        </ActionButton>
                      )}

                      {(payment.status === "pending" ||
                        payment.status === "overdue" ||
                        payment.status === "failed") && (
                        <ActionButton
                          type="default"
                          icon={<CheckCircleOutlined />}
                          onClick={() => onMarkPaid(payment.id)}
                        >
                          Mark Paid
                        </ActionButton>
                      )}

                      {(() => {
                        const ps = (payment.status || "").toLowerCase();
                        if (
                          ps === "paid" ||
                          ps === "processing" ||
                          ps === "succeeded"
                        )
                          return null;
                        return (
                        <ActionButton
                          type="default"
                          icon={<MessageOutlined />}
                          loading={sendingReminderToPaymentId === payment.id}
                          onClick={() => {
                            console.log(
                              "[DEBUG] Send reminder button clicked in mobile view for payment:",
                              payment.id
                            );
                            if (onSendReminder) {
                              onSendReminder(payment.id);
                            }
                          }}
                        >
                          Remind
                        </ActionButton>
                        );
                      })()}
                    </ActionButtons>
                  </CardContent>
                </PaymentCard>
              ))}
            </MobilePaymentCards>
          )
        ) : (
          <Table
            columns={columns}
            dataSource={payments}
            loading={loading}
            rowKey="id"
            rowClassName={getRowClassName}
            locale={{
              emptyText: hasPaymentsButFiltered
                ? renderFilteredEmptyState()
                : renderEmptyState(),
            }}
            pagination={{
              pageSize: pageSize,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total} payments`,
              onShowSizeChange: handlePageSizeChange,
              pageSizeOptions: ["10", "20", "50", "100"],
            }}
            sortDirections={["descend", "ascend"]}
          />
        )}
      </Card>

      <DetailsModal
        open={isDetailsModalVisible}
        onCancel={handleCloseDetailsModal}
        title="Payment Details"
        icon={
          <DollarOutlined
            style={{ color: "white", filter: "brightness(0) invert(1)" }}
          />
        }
        data={selectedPayment}
        type="payment"
        actionButtons={selectedPayment ? getActionButtons(selectedPayment) : []}
        showActions={false}
        relatedData={{ tenants, units }}
      />

      <DetailsModal
        open={isTenantModalVisible}
        onCancel={handleCloseTenantModal}
        title="Tenant Details"
        icon={
          <UserOutlined
            style={{ color: "white", filter: "brightness(0) invert(1)" }}
          />
        }
        data={selectedTenant}
        type="tenant"
        showActions={false}
        relatedData={{ units }}
      />

      <DetailsModal
        open={isUnitModalVisible}
        onCancel={handleCloseUnitModal}
        title="Door Details"
        icon={
          <HomeOutlined
            style={{ color: "white", filter: "brightness(0) invert(1)" }}
          />
        }
        data={selectedUnitForModal}
        type="unit"
        showActions={false}
        relatedData={{ tenants }}
      />

      <InvoiceAdjustmentModal
        visible={isAdjustmentModalVisible}
        onCancel={handleCloseAdjustmentModal}
        onSuccess={handleAdjustmentSuccess}
        invoice={selectedPaymentForAdjustment}
        invoiceId={selectedPaymentForAdjustment?.id}
        tenantName={
          tenants.find((t) => t.id === selectedPaymentForAdjustment?.tenantId)
            ?.name
        }
        tenants={tenants}
      />
    </TableSection>
  );
};

export default PaymentDetailsSection;

const FiltersRow = styled(Row)`
  margin-bottom: 1.5rem;
  padding-left: 2rem;
  padding-right: 2rem;

  @media (max-width: 768px) {
    padding-left: 1rem;
    padding-right: 1rem;
    margin-bottom: 1.25rem;
  }
`;

const FilterInput = styled(RangePicker)`
  width: 100%;
  height: 100%;
  border-radius: 8px;
  border: 1px solid var(--color-border-light);
  font-size: 0.95rem;

  @media (max-width: 768px) {
    height: 44px;
    font-size: 1rem;
  }

  &:hover,
  &:focus {
    border-color: var(--color-primary);
    box-shadow: 0 0 0 2px var(--color-primary-glow);
  }
`;

const FilterSelect = styled(Select)`
  width: 100%;

  .ant-select-selector {
    height: 40px;
    border-radius: 8px;
    border: 1px solid var(--color-border-light);
    padding: 8px 12px;
    display: flex;
    align-items: center;
    font-size: 0.95rem;
  }

  @media (max-width: 768px) {
    .ant-select-selector {
      height: 44px;
      font-size: 1rem;
      padding: 10px 12px;
    }
  }

  .ant-select-arrow {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: var(--color-text-light);
    font-size: 12px;
    transition: all 0.2s ease;
    margin-top: 0.05rem !important;
  }

  @media (max-width: 768px) {
    .ant-select-arrow {
      font-size: 14px;
      right: 14px;
    }
  }

  &:hover .ant-select-selector,
  &.ant-select-focused .ant-select-selector {
    border-color: var(--color-primary);
    box-shadow: 0 0 0 2px var(--color-primary-glow);
  }

  &.ant-select-focused .ant-select-arrow {
    color: var(--color-primary);
  }
`;

const AddPaymentButton = styled(Button)`
  background: transparent !important;
  border: none !important;
  color: white !important;
  font-weight: 500 !important;
  height: 36px !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;

  .anticon {
    color: white !important;
    filter: brightness(0) invert(1) !important;
  }

  &:hover,
  &:focus {
    // background: rgba(255, 255, 255, 0.2) !important;
    // border: 1px solid rgba(255, 255, 255, 0.3) !important;
    background: transparent !important;
    color: white !important;
    transform: translateY(-1px) !important;
    backdrop-filter: blur(10px);
  }

  &:active {
    background: rgba(255, 255, 255, 0.15) !important;
    transform: translateY(0) !important;
  }
`;

const TableSection = styled.div`
  margin-bottom: 2rem;
  width: 100%;

  .ant-card {
    border-radius: 16px;
    overflow: hidden;
    box-shadow: 0 6px 24px rgba(44, 95, 45, 0.08);
    border: 2px solid var(--color-border-light);
    width: 100%;
  }

  .ant-table {
    min-width: unset !important;
  }

  .ant-table-tbody > tr > td {
    white-space: nowrap;
  }

  .ant-table-tbody > tr > td:first-child {
    white-space: normal;
    max-width: 200px;
  }

  .ant-table-tbody > tr > td:nth-child(2) {
    white-space: normal;
    word-wrap: break-word;
    max-width: 250px;
  }

  .ant-card-head {
    background: var(--color-primary) !important;
    border-bottom: 2px solid var(--color-border-light);
    padding: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    position: relative;
  }

  .ant-card-head-title {
    color: white !important;
    font-family: var(--font-primary);
    font-weight: 600;
    font-size: 1.25rem;
    padding: 1.5rem 2rem 1rem 2rem;
    text-align: center;
    flex: 1;
  }

  .ant-card-extra {
    position: absolute;
    right: 0;
    top: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    padding-right: 2rem;
  }

  .ant-table {
    border-radius: 16px;
    overflow: hidden;
    box-shadow: 0 6px 24px rgba(44, 95, 45, 0.08);
    border: 2px solid var(--color-border-light);
    width: 100%;
    table-layout: fixed;
  }

  .ant-table-thead > tr > th {
    background: var(--color-bg-tertiary);
    border-bottom: 2px solid var(--color-border-light);
    font-family: var(--font-secondary);
    font-weight: 600;
    color: #ffffff;
    padding: 1.25rem 1rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .ant-table-tbody > tr > td {
    padding: 1.25rem 1rem;
    border-bottom: 1px solid var(--color-border-light);
    font-family: var(--font-secondary);
    overflow: hidden;
    text-overflow: ellipsis;
    word-wrap: break-word;
    vertical-align: middle;
  }

  .ant-table-tbody > tr:hover > td {
    background: var(--color-primary-glow);
  }

  .ant-table-tbody > tr:last-child > td {
    border-bottom: none;
  }

  .ant-tag {
    border-radius: 8px;
    font-weight: 600;
    font-size: 0.875rem;
    padding: 0.25rem 0.75rem;
    border: none;
  }

  /* Processing/Created tag - light blue background with blue text and icon */
  .ant-tag-processing {
    background: rgba(59, 130, 246, 0.12) !important;
    border: 1px solid rgba(59, 130, 246, 0.4) !important;
    color: #2563eb !important;
  }

  .ant-tag-processing,
  .ant-tag-processing *,
  .ant-tag-processing .anticon,
  .ant-tag-processing span,
  .ant-tag-processing .ant-tag-text {
    color: #2563eb !important;
  }

  .ant-tag-processing .anticon {
    color: #3b82f6 !important;
  }

  .ant-btn-text {
    border-radius: 8px;
    transition: all 0.2s ease;

    &:hover {
      background: var(--color-primary-glow);
      color: var(--color-primary);
    }
  }

  /* Fix pagination alignment */
  .ant-pagination {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  .ant-pagination-total-text {
    display: flex;
    align-items: center;
    height: 32px;
    line-height: 32px;
  }

  .ant-pagination-item,
  .ant-pagination-prev,
  .ant-pagination-next {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 32px;
    min-width: 32px;
    line-height: 32px;
  }

  .ant-pagination-options {
    display: flex;
    align-items: center;
    height: 32px;
    margin-left: 0;
    position: relative;
    z-index: 1;

    .ant-select {
      display: flex;
      align-items: center;
      height: 32px;
      position: relative;
      z-index: 100;
    }

    .ant-select-selector {
      display: flex;
      align-items: center;
      height: 32px !important;
      padding: 0 11px;
    }

    .ant-select-selection-item {
      display: flex;
      align-items: center;
      line-height: 32px;
      height: 32px;
    }

    .ant-select-arrow {
      display: flex;
      align-items: center;
      top: 50%;
      transform: translateY(-50%);
      margin-top: 0 !important;
    }
  }

  /* Prevent table scroll when pagination dropdown is open */
  .ant-table-pagination {
    position: relative;
    z-index: 10;
  }

  /* Prevent table body from scrolling when pagination dropdown opens */
  .ant-table-body {
    scroll-behavior: auto !important;
    overflow-anchor: none !important;
  }

  /* Prevent scroll on table container */
  .ant-table-container {
    scroll-behavior: auto !important;
    overflow-anchor: none !important;
  }
`;

// Mobile Card Components
const MobilePaymentCards = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 0.5rem 0;
`;

const PaymentCard = styled.div`
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 50%, #f1f5f9 100%);
  border-radius: 16px;
  border: 2px solid
    ${(props) => {
      switch (props.$status) {
        case "paid":
          return "#d9f7be";
        case "pending":
          return "#fff7e6";
        case "processing":
          return "#dbeafe";
        case "overdue":
          return "#ffccc7";
        default:
          return "#e2e8f0";
      }
    }};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  transition: all 0.3s ease;
  position: relative;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  }

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${(props) => {
      switch (props.$status) {
        case "paid":
          return "linear-gradient(90deg, #52c41a 0%, #73d13d 100%)";
        case "pending":
          return "linear-gradient(90deg, #faad14 0%, #ffc53d 100%)";
        case "processing":
          return "linear-gradient(90deg, #1e3a8a 0%, #3b82f6 100%)";
        case "overdue":
          return "linear-gradient(90deg, #ff4d4f 0%, #ff7875 100%)";
        default:
          return "linear-gradient(90deg, #d9d9d9 0%, #f0f0f0 100%)";
      }
    }};
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.25rem 1.5rem 1rem;
  border-bottom: 1px solid #e2e8f0;
`;

const StatusIndicator = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${(props) => {
    switch (props.$status) {
      case "paid":
        return "linear-gradient(135deg, #52c41a 0%, #73d13d 100%)";
      case "pending":
        return "linear-gradient(135deg, #faad14 0%, #ffc53d 100%)";
      case "processing":
        return "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)";
      case "overdue":
        return "linear-gradient(135deg, #ff4d4f 0%, #ff7875 100%)";
      default:
        return "linear-gradient(135deg, #d9d9d9 0%, #f0f0f0 100%)";
    }
  }};
  color: white;
  font-size: 1.25rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
`;

const CardTitle = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const TenantInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  color: var(--color-primary);
  font-weight: 600;
  font-size: 1rem;
  transition: all 0.2s ease;

  &:hover {
    color: #2d5a2d;
    text-decoration: underline;
  }

  .anticon {
    font-size: 1rem;
  }
`;

const UnitInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  color: #64748b;
  font-weight: 500;
  font-size: 0.9rem;
  transition: all 0.2s ease;

  &:hover {
    color: var(--color-primary);
    text-decoration: underline;
  }

  .anticon {
    font-size: 0.9rem;
  }
`;

const CardContent = styled.div`
  padding: 1rem 1.5rem 1.5rem;
`;

const AmountSection = styled.div`
  text-align: center;
  margin-bottom: 1.5rem;
  padding: 1rem;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-radius: 12px;
  border: 1px solid #e2e8f0;
`;

const AmountValue = styled.div`
  font-size: 2rem;
  font-weight: 800;
  color: var(--color-primary);
  margin-bottom: 0.25rem;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
`;

const AmountLabel = styled.div`
  font-size: 0.85rem;
  color: #64748b;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0.25rem;
`;

const AdjustmentsSummary = styled.div`
  font-size: 0.8rem;
  color: #8c8c8c;
  margin-top: 0.5rem;
  margin-bottom: 0.5rem;
`;

const MobileAdjustmentsList = styled.div`
  text-align: left;
  margin-top: 0.25rem;
  padding-top: 0.5rem;
  border-top: 1px solid #e2e8f0;
`;

const MobileAdjustmentRow = styled.div`
  font-size: 0.8rem;
  color: #475569;
  padding: 0.25rem 0;
  line-height: 1.35;
`;

const DetailsSection = styled.div`
  margin-bottom: 1.5rem;
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 0;
  border-bottom: 1px solid #f1f5f9;

  &:last-child {
    border-bottom: none;
  }
`;

const DetailLabel = styled.div`
  font-size: 0.9rem;
  color: #64748b;
  font-weight: 600;
`;

const DetailValue = styled.div`
  font-size: 0.9rem;
  color: #1e293b;
  font-weight: 500;
`;

const StatusDetailRow = styled.div`
  padding: 0.75rem 0;
  border-bottom: 1px solid #f1f5f9;
  text-align: center;

  &:last-child {
    border-bottom: none;
  }
`;

const StatusDetailLabel = styled.div`
  font-size: 0.9rem;
  color: #64748b;
  font-weight: 600;
  margin-bottom: 0.5rem;
`;

const StatusDetailContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  align-items: center;
`;

const StatusDescription = styled.div`
  font-size: 0.8rem;
  color: #64748b;
  line-height: 1.4;
  margin-top: 0.25rem;
`;

const StatusTag = styled(Tag)`
  margin: 0;
  font-weight: 600;
  border-radius: 8px;
  padding: 0.35rem 0.85rem;
  width: fit-content;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;

  /* Blue: processing, pending, created, pending_verification */
  &.ant-tag-processing,
  &.ant-tag-warning {
    background: #dbeafe !important;
    background-color: #dbeafe !important; // Override inline styles
    border: 1px solid #60a5fa !important;
    border-color: #60a5fa !important; // Override inline styles
    color: #1e40af !important;

    .anticon {
      color: #2563eb !important;
    }
  }

  /* Success tag (paid, succeeded) - cohesive green */
  &.ant-tag-success {
    background: #d1fae5 !important;
    border: 1px solid #10b981 !important;
    color: #065f46 !important;

    .anticon {
      color: #059669 !important;
    }
  }

  /* Error tag (failed, overdue, canceled) - dark red with white text */
  &.ant-tag-error {
    background: #dc2626 !important; // Dark red background for white text
    border: 1px solid #b91c1c !important; // Darker red border
    color: #ffffff !important; // White text

    .anticon {
      color: #ffffff !important; // White icon
    }
  }

  /* Default tag (refunded) - cohesive gray */
  &.ant-tag-default {
    background: #f3f4f6 !important;
    border: 1px solid #d1d5db !important;
    color: #374151 !important;

    .anticon {
      color: #6b7280 !important;
    }
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.75rem;
  }
`;

const ActionButton = styled(Button)`
  flex: 1;
  min-width: 120px;
  height: 40px;
  border-radius: 10px;
  font-weight: 600;
  font-size: 0.9rem;
  transition: all 0.2s ease;

  @media (max-width: 768px) {
    width: 100%;
    flex: none;
    min-width: unset;
  }

  &.ant-btn-primary {
    background: linear-gradient(135deg, var(--color-primary) 0%, #2d5a2d 100%);
    border: none;
    box-shadow: 0 4px 12px rgba(44, 95, 45, 0.25);

    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 6px 16px rgba(44, 95, 45, 0.35);
      background: linear-gradient(
        135deg,
        #2d5a2d 0%,
        var(--color-primary) 100%
      );
    }
  }

  &.ant-btn-default {
    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
    border: 1px solid #e2e8f0;
    color: #64748b;

    &:hover {
      background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
      border-color: var(--color-primary);
      color: var(--color-primary);
      transform: translateY(-1px);
    }
  }
`;
